import { action, computed } from "@ember/object"
import type ObjectProxy from "@ember/object/proxy"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { User } from "@shared/models/user"
import { handleError } from "cc-frontend/error-handling"
import ENV from "cc-frontend/config/environment"
import findDocument from "cc-frontend/helpers/find-document"
import userChangeHomescreenPatternAction from "cc-frontend/lib/actions/planbook/USER_CHANGE_DASHBOARD_PATTERN"
import type Ajax from "cc-frontend/services/ajax"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type NotifierService from "cc-frontend/services/notifier"
import type Rpc from "cc-frontend/services/rpc"
import type Session from "cc-frontend/services/session"
import { heroPatterns } from "cc-frontend/utils/planbook-covers"
import _ from "lodash"
import { first } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./account.module.css"

interface Args {
  order: number
  closeModal: Function
  model: {
    userId: string
  }
}

export default class UserAccount extends Component<Args> {
  readonly styles = styles

  @service declare ajax: Ajax
  @service declare rpc: Rpc
  @service declare dispatcher: Dispatcher
  @service("ember-store") declare store: EmberStore
  @service declare session: Session
  @service declare modalManager: ModalManager
  @service declare notifier: NotifierService

  @tracked showAllPatterns = false
  @tracked couponCode = ""
  @tracked isApplyingCoupon = false

  // The available colors. These are stored in the same place as the planbook backgrounds,
  // albeit with a .webp extension.
  solidColors = ["yellow", "turquoise", "cyan", "green", "blue", "purple", "coral", "pink"]
  homescreenPatterns = heroPatterns

  @findDocument("args.model.userId", "user") declare user: ObjectProxy<User>

  @computed("user.content.attributes.subscriptions")
  get lastSubscription() {
    let subscriptions = this.user.content?.attributes?.subscriptions
    let lastSubscription = _.chain(subscriptions).sortBy("startedAt").last().value()

    return lastSubscription
  }

  @action
  updateCouponCode(event: Event) {
    this.couponCode = (event.target as HTMLInputElement).value
  }

  @action
  async applyCoupon(event: Event) {
    event.preventDefault()

    const userId = this.args.model.userId
    const coupon = this.couponCode.trim()
    if (coupon === "" || this.isApplyingCoupon) return

    this.isApplyingCoupon = true

    try {
      const response = await this.rpc.user_apply_coupon({ userId, coupon })

      if (response.responseOk) {
        this.couponCode = ""
        this.notifier.success("", {
          notificationId: userId,
          title: "Coupon applied!",
          message: "It'll show up on your next invoice.",
          duration: 5000,
        })
      } else {
        this.notifier.warning("", {
          notificationId: userId,
          title: response.result.message,
          message: response.result.contactSupport
            ? "Contact us through the message box below."
            : "Double-check the code and try again.",
          duration: 5000,
        })
      }
    } catch (error) {
      if (error instanceof Error) handleError(error)
      this.notifier.warning("", {
        notificationId: userId,
        title: "Something went wrong applying that coupon.",
        message: "Contact us through the message box below.",
        duration: 5000,
      })
    } finally {
      this.isApplyingCoupon = false
    }
  }

  @action
  usePattern(pattern: string) {
    this.dispatcher.dispatchPreparedAction(
      userChangeHomescreenPatternAction({
        userId: this.args.model.userId,
        originalHomescreenPattern: this.user.content?.attributes.homescreenPattern ?? null,
        homescreenPattern: pattern,
      })
    )
  }

  /**
   *
   * @param color A color from this.solidColors (without the extension or Imgix URL parts).
   */
  @action
  updateColor(color: string) {
    this.dispatcher.dispatch("USER_CHANGE_AVATAR_COLOR", {
      userId: this.user.content?.id,
      value: color,
    })
  }

  @action
  updateName(userId, field, value) {
    this.dispatcher.dispatch("USER_CHANGE_NAME", {
      userId: userId,
      field: field,
      value: value,
    })
  }

  // @action cancelSubscription() {
  //   if (window && window.churnkey) {
  //     try {
  //       window.churnkey.init("show", {
  //         customerId: this.user.content?.attributes?.stripeId,
  //         authHash: this.user.content?.attributes?.hmacChurnkey,
  //         appId: "81ycuhyqm",
  //         mode: "live",
  //         provider: "stripe",
  //         handleSupportRequest: (customer) => {
  //           console.log(customer)
  //           window.Intercom("showNewMessage", "Attention: Customer Needs Help ASAP.\n")
  //           window.churnkey.hide()
  //         },
  //       })
  //     } catch (error) {
  //       window.Intercom("showNewMessage", `I'd like to cancel my subscription.`)
  //     }
  //   } else if (window && window.Intercom) {
  //     window.Intercom("showNewMessage", `I'd like to cancel my subscription.`)
  //   } else {
  //     window.alert(
  //       "Your browser has blocked our self-serve cancellation screen. Email support@commonplanner.com and we'll get this canceled for you!"
  //     )
  //   }
  // }

  @action updateEmail(email) {
    // This is not how I'd write if I had more time. I'd use a more robust flow
    if (this.user.content?.attributes?.email === email) return
    return this.ajax
      .post(ENV.API_ROOT + "/users/change_email", {
        dataType: "json",
        contentType: "application/json; charset=utf-8",
        body: JSON.stringify({
          email: email,
        }),
      })
      .then((_data) => {
        swal({
          title: "Email changed!",
          type: "success",
        }).catch((error) => {
          if (error instanceof Error) handleError(error)
        })
      })
      .catch((_data) => {
        swal({
          title: "Sorry! That email is already taken",
          type: "error",
        }).catch((error) => {
          if (error instanceof Error) handleError(error)
        })
      })
  }

  get initials() {
    if (this.user.content) {
      let firstI = first(this.user.content.attributes.firstName || " ")
      let lastI = first(this.user.content.attributes.lastName || " ")
      return `${firstI}${lastI}`
    }
    return ""
  }
}
